import {
  Appointments,
  Discounts,
  PaymentMethod,
  Payments,
  PaymentStatus,
  PaymentType,
} from '@prisma/client';
import { Payment, PaymentMetadata } from '../../types/reports';
import {
  Appointment,
  CheckoutFailureData,
  Customer,
  LoadedUser,
  Transaction,
} from '../../interfaces';
import {
  InitiatePaymentPayload,
  PaymentsGroup,
  PaymentsSplit,
  PaymentWithTransfers,
  SplitPaymentForm,
} from '../../types/payments';
import {
  DEFAULT_CUSTOMER_SERVICE_FEE,
  DEFAULT_PER_CHARGE_CENTS_FEES,
} from '../../constants/global';
import { OrderWithItems, OrderWithPayments } from '../../types/orders';
import {
  convertSnakeToTitleCase,
  formatDollarAmount,
  isNil,
} from '../../utils/global/helper';
import {
  CheckoutFormValues,
  CheckoutPaymentMethod,
} from '../../types/appointments';
import { getUserNameFromBusiness } from '../global';
import { isAppointmentDepositPaid } from '../appointments';
import { SplitPaymentMethod } from '../../context/SplitPaymentsContext/context';
import { FailureData } from '../../hooks/payments/useCheckoutSocket';
import { MINIMUM_FEE_CENTS } from '../../constants/payments';

const PAID_STATUSES = ['SUCCESS', 'REFUNDED', 'VOIDED'] as PaymentStatus[];

export function isPaymentComplete(payment: Pick<Payment, 'status'>) {
  return PAID_STATUSES.includes(payment.status);
}

export function getMetadataFromPayment(
  payment: Payments | Payment,
): PaymentMetadata {
  if (!payment?.metadata) return {};
  const metadata = payment.metadata || {};
  if (typeof metadata === 'string') {
    return JSON.parse(metadata) as PaymentMetadata;
  }
  return metadata as PaymentMetadata;
}

export function getPaymentAmountWithoutServiceFee(payment: Payments | Payment) {
  let { amount } = payment;
  const metadata = getMetadataFromPayment(payment);
  if (metadata.service_fee) {
    amount -= parseInt(String(metadata.service_fee), 10);
  }
  amount /= 100.0;
  return amount;
}

export function getPaymentAmountWithoutServiceFeeOrSalesTax(
  payment: Payments | Payment,
) {
  const paymentWithoutFee = getPaymentAmountWithoutServiceFee(payment);
  const metadata = getMetadataFromPayment(payment);
  if (metadata?.sales_tax) {
    return paymentWithoutFee - (metadata.sales_tax || 0) / 100;
  }
  return paymentWithoutFee;
}

export function convertPaymentsToTransactions(
  payments: Payments[],
  extra: {
    appointment?: Appointment | null;
    order?: OrderWithPayments | null;
    paymentsGroup?: PaymentsGroup | null;
    customer?: Customer;
  } = {},
) {
  const cleanedTransactions = payments.map(payment => {
    const metadata = getMetadataFromPayment(payment);
    let amount = getPaymentAmountWithoutServiceFee(payment);

    // Convert stripe UNIX timestamp to date
    const dateCreated = new Date(payment.completedAt || payment.updatedAt);
    const dateUpdated = new Date(
      payment.updatedAt || payment.completedAt || payment.createdAt,
    );

    let tipAmt = metadata.tip_amt ? metadata.tip_amt / 100 : 0;

    if (metadata?.tip_amt) {
      tipAmt = metadata.tip_amt / 100;
      if (!isPaymentComplete(payment)) {
        amount -= tipAmt;
      }
    }

    return {
      id: payment.id,
      amount,
      dateCreated,
      dateUpdated,
      type: payment.method,
      tipAmount: tipAmt,
      chargeReason: metadata?.charge_reason,
      status: payment.status,
      error: payment.error || undefined,
      payment,
      discountAmount: (payment.discountAmount || 0) / 100,
      ...extra,
    } as Transaction;
  });
  return cleanedTransactions.sort(
    (a, b) => b.dateCreated.valueOf() - a.dateCreated.valueOf(),
  );
}

export function isCardPaymentMethod(
  paymentMethod: PaymentMethod | CheckoutPaymentMethod | undefined | null,
) {
  return (
    !!paymentMethod && ['CREDIT', 'TERMINAL', 'MANUAL'].includes(paymentMethod)
  );
}

export function getPaymentSplitsFromPayments(payments: PaymentWithTransfers[]) {
  const successPayments = payments.filter(
    payment => isPaymentComplete(payment) && !!payment.transfers?.length,
  );
  const paymentsSplit = successPayments.reduce<PaymentsSplit[]>(
    (list, payment) => {
      const currentPaymentList: PaymentsSplit[] = [];
      let paymentAmount = getPaymentAmountWithoutServiceFee(payment);
      let transferTotal =
        payment.transfers?.reduce(
          (acc, transfer) => acc + transfer.amount / 100,
          0,
        ) ?? 0;

      const metadata = getMetadataFromPayment(payment);
      const tipAmount = metadata.tip_amt ? metadata.tip_amt / 100 : 0;

      if (tipAmount) {
        const perChargeFeeCents =
          metadata.per_charge_fee_cents ?? DEFAULT_PER_CHARGE_CENTS_FEES;
        const perChargeFee = perChargeFeeCents / 100;

        const feesPaidFromPercentage =
          paymentAmount - transferTotal - perChargeFee;

        const feePercentage = feesPaidFromPercentage / paymentAmount;

        const tipFees = tipAmount * feePercentage;
        const tipTransferAmount = tipAmount - tipFees;

        paymentAmount -= tipAmount;
        transferTotal -= tipTransferAmount;

        currentPaymentList.push({
          id: payment.id,
          type: 'tip',
          amount: tipAmount,
          transfersAmount: tipTransferAmount,
        });
      }

      currentPaymentList.unshift({
        id: payment.id,
        type: 'payment',
        amount: paymentAmount,
        transfersAmount: transferTotal,
      });

      return [...list, ...currentPaymentList];
    },
    [],
  );

  return paymentsSplit;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function getTransactionCurrency(_transaction: Transaction) {
  return 'USD';
}

export function getPaymentMaxRefundAmount(
  payment: Payments | null | undefined,
) {
  if (!payment) return 0;

  const metadata = getMetadataFromPayment(payment);
  if (isNil(metadata.service_fee)) {
    return payment.amount / 100;
  }
  return getPaymentAmountWithoutServiceFee(payment);
}

export function isTipAllowed(paymentMethod: CheckoutPaymentMethod) {
  return ['CASH', 'MANUAL', 'GIFT_CARD'].includes(paymentMethod);
}

export function isDiscountAllowed(paymentMethod: CheckoutPaymentMethod) {
  return ['CASH', 'MANUAL', 'TERMINAL', 'GIFT_CARD'].includes(paymentMethod);
}

// GIFT_CARD last
export function sortPaymentsByPaymentMethod(payments: (Payments | Payment)[]) {
  return payments.sort((a, b) => {
    if (a.method === 'GIFT_CARD') return 1;
    if (b.method === 'GIFT_CARD') return -1;
    return 0;
  });
}

export function getPaymentWithMetadata(
  payments: (Payments | Payment)[],
  type?: PaymentType,
) {
  const foundPayment = payments.find(
    payment =>
      (!type || payment.type === type) &&
      !!Object.keys(getMetadataFromPayment(payment))?.length,
  );
  if (!foundPayment) return null;

  return {
    ...foundPayment,
    metadata: getMetadataFromPayment(foundPayment),
  } as Payment;
}

export function getProcessedByName(user: LoadedUser, transaction: Transaction) {
  const { customer } =
    transaction.appointment ||
    transaction.order ||
    transaction.payment ||
    transaction;

  const tempCustomerId =
    !!customer && 'customerId' in customer
      ? customer.customerId
      : customer?.userId;

  const isProcessedByCustomer =
    tempCustomerId === transaction.payment?.processedByUserId;

  const userNameToShow = getUserNameFromBusiness(
    user,
    isProcessedByCustomer,
    transaction.payment?.processedByUserId,
  );

  return userNameToShow;
}

export function getInitiatePaymentPayload({
  appointmentId,
  businessId,
  discount,
  values,
  failureData,
}: {
  appointmentId?: string | null;
  businessId: string;
  discount?: Discounts | null;
  values: CheckoutFormValues;
  failureData?: CheckoutFailureData | null;
}): {
  data: InitiatePaymentPayload;
  overridePaymentMethod: PaymentMethod | undefined;
} {
  let overridePaymentMethod: PaymentMethod | undefined;

  const data: InitiatePaymentPayload = {
    businessId,
    customerId: values.customer.customerId,
    isCashTaxed: values.isCashTaxed,
    isManualCheckout: false,
    appointment: appointmentId
      ? ({
          appointmentId,
          appointmentPrice: values.price,
          includeBonus: values.includeBonus,
          isNoCharge: false,
        } as NonNullable<InitiatePaymentPayload['appointment']>)
      : undefined,
    order: values.customOrderItems?.length
      ? {
          employeeId: values.employeeId || undefined,
          orderItems: values.customOrderItems.map(i => ({
            itemId: i.itemId,
            quantity: i.quantity,
            name: i.name,
            price: i.price,
          })),
          orderIdToDelete: values.failedOrderId,
        }
      : undefined,
    tipAmount: 0,
    paymentIntentId: values.paymentIntentId || failureData?.paymentIntentId,
    paymentMethod: values.paymentMethod as PaymentMethod,
  };

  if (values.paymentMethod === 'MANUAL') {
    data.paymentMethod = 'CREDIT';
    data.isManualCheckout = true;
  }

  if (data.appointment && values.paymentMethod === 'FREE') {
    data.appointment.isNoCharge = true;
    data.paymentMethod = 'CASH';
  }

  if (
    data.appointment &&
    !data.appointment.isNoCharge &&
    values.price <= 0 &&
    values.customOrderItems?.length
  ) {
    data.appointment.isNoCharge = true;
  }

  if (isTipAllowed(values.paymentMethod) && values.tipAmt) {
    data.tipAmount = values.tipAmt;
  }

  if (isDiscountAllowed(values.paymentMethod) && discount) {
    data.discountId = discount.id;
  }

  return { data, overridePaymentMethod };
}

export function checkPaymentType(
  payment: Pick<Payments | Payment, 'type' | 'types'> | null | undefined,
  type: PaymentType | PaymentType[],
) {
  if (!payment) return false;
  const paymentTypes = [payment.type, ...(payment.types ?? [])];
  if (Array.isArray(type)) {
    return type.some(t => paymentTypes.includes(t));
  }
  return paymentTypes.includes(type);
}

export function getPaymentsTipAmount(payments: (Payments | Payment)[]) {
  return (
    (payments?.reduce(
      (acc, payment) =>
        acc + (+(getMetadataFromPayment(payment).tip_amt ?? 0) || 0),
      0,
    ) || 0) / 100
  );
}

export function getPaymentsTotalDiscountedAmount(payments: Payments[]) {
  return (
    (payments?.reduce((acc, payment) => acc + payment.discountAmount, 0) || 0) /
    100
  );
}

export function getPaymentLineItems({
  appointment,
  order,
  payments: passedPayments,
}: {
  appointment: Appointment | Appointments | null;
  order: OrderWithItems | null;
  payments: PaymentWithTransfers[];
}) {
  const lineItems: { amount: number; label: string; isTotal?: boolean }[] = [];

  const isSingleIncompletePayment =
    passedPayments.length === 1 && passedPayments[0]?.status === 'PENDING';
  if (isSingleIncompletePayment) {
    const singleIncompletePaymentAmount =
      getPaymentAmountWithoutServiceFeeOrSalesTax(passedPayments[0]) -
      (getMetadataFromPayment(passedPayments[0])?.tip_amt ?? 0) / 100;
    lineItems.push(
      {
        amount: singleIncompletePaymentAmount,
        label: 'Pending Total',
      },
      {
        amount: 0,
        label: 'Total Paid',
        isTotal: true,
      },
    );
    return lineItems;
  }

  const payments = passedPayments.filter(payment => isPaymentComplete(payment));
  // if import payment is present, we do not have access to the `type` field
  const hasImportPayment = payments.some(p => checkPaymentType(p, 'IMPORT'));
  if (hasImportPayment) {
    lineItems.push({
      amount: payments.reduce(
        (acc, payment) =>
          acc +
          getPaymentAmountWithoutServiceFeeOrSalesTax(payment) -
          (getMetadataFromPayment(payment)?.tip_amt || 0) / 100,
        0,
      ),
      label: appointment ? 'Appointment Price' : 'Total Price',
    });
  } else {
    const hasApptDepositPayment = payments.some(p =>
      checkPaymentType(p, 'DEPOSIT'),
    );
    const hasApptFullPayment = payments.some(p => checkPaymentType(p, 'FULL'));
    const hasApptReschedulingFeePayment = payments.some(p =>
      checkPaymentType(p, 'RESCHEDULE'),
    );
    const hasApptPayment =
      !!appointment &&
      (hasApptDepositPayment ||
        hasApptFullPayment ||
        hasApptReschedulingFeePayment);

    if (hasApptPayment && appointment) {
      const isDepositPaidSeparately = isAppointmentDepositPaid(appointment);
      const refundedDeposit = payments.find(
        p => checkPaymentType(p, 'DEPOSIT') && p.status === 'REFUNDED',
      );

      if (hasApptDepositPayment && hasApptFullPayment) {
        lineItems.push({
          amount: appointment.price || 0,
          label: 'Appointment Price',
        });
      } else {
        if (hasApptDepositPayment) {
          const refundedMd = refundedDeposit
            ? getMetadataFromPayment(refundedDeposit)
            : null;
          const refundedDepositAmount =
            refundedMd && refundedDeposit
              ? getPaymentAmountWithoutServiceFeeOrSalesTax(refundedDeposit)
              : 0;
          lineItems.push({
            amount: appointment.depositPrice || refundedDepositAmount || 0,
            label: 'Deposit Price',
          });
        }
        if (hasApptFullPayment) {
          lineItems.push({
            amount:
              (appointment.price || 0) -
              (isDepositPaidSeparately ? appointment.depositPrice || 0 : 0),
            label: 'Appointment Price',
          });
        }
      }

      const reschedulePayments = payments.filter(p =>
        checkPaymentType(p, 'RESCHEDULE'),
      );
      const reschedulingFeesPaid = reschedulePayments?.length
        ? reschedulePayments.reduce(
            (acc, p) => acc + getPaymentAmountWithoutServiceFeeOrSalesTax(p),
            0,
          )
        : 0;
      if (reschedulingFeesPaid) {
        lineItems.push({
          amount: reschedulingFeesPaid,
          label: 'Rescheduling Fee(s) Paid',
        });
      }
    }

    if (order?.orderItems?.length) {
      order.orderItems.forEach(orderItem => {
        lineItems.push({
          amount: (orderItem.customItemPrice || 0) * orderItem.quantity,
          label: `${
            orderItem.item?.name || orderItem?.customItemName || 'Custom Item'
          }${orderItem.quantity > 1 ? `  (${orderItem.quantity})` : ''}`,
        });
      });
    }
  }

  const tipAmountPaid = getPaymentsTipAmount(payments);
  if (tipAmountPaid)
    lineItems.push({
      amount: tipAmountPaid,
      label: 'Tip ',
    });

  const taxesPaid = payments.reduce(
    (acc, payment) =>
      acc + Number(getMetadataFromPayment(payment)?.sales_tax || 0) / 100,
    0,
  );
  if (taxesPaid) {
    lineItems.push({
      amount: taxesPaid,
      label: 'Sales Tax',
    });
  }

  const discountedAmount = getPaymentsTotalDiscountedAmount(payments);
  if (discountedAmount > 0) {
    lineItems.push({
      amount: -discountedAmount,
      label: 'Discount',
    });
  }

  // if gift card is a part of the payment, subtract it from the total
  const giftCardAmountCents = getMetadataFromPayment(payments[0])
    ?.gift_card_amount_cents;
  const giftCardAmount =
    payments.length === 1 && giftCardAmountCents
      ? giftCardAmountCents / 100
      : 0;
  if (giftCardAmount) {
    lineItems.push({
      amount: -giftCardAmount,
      label: 'Gift Card',
    });
  }

  if (payments.length === 1 && payments[0]?.method === 'GIFT_CARD') {
    const totalAmount = lineItems.reduce((acc, item) => acc + item.amount, 0);
    const otherPaymentsAmount = totalAmount - payments[0].amount / 100;
    if (otherPaymentsAmount > 0) {
      lineItems.push({
        amount: -otherPaymentsAmount,
        label: 'Other Payments',
      });
    }
  }

  const refundedAmount = payments
    .filter(p => p.status === 'REFUNDED')
    .reduce((acc, payment) => {
      // if deposit was refunded prior to full payment, do not include in line item
      const isDepositBeforeFull =
        checkPaymentType(payment, 'DEPOSIT') &&
        !isAppointmentDepositPaid(appointment);
      return (
        acc +
        (isDepositBeforeFull ? 0 : getPaymentAmountWithoutServiceFee(payment))
      );
    }, 0);
  if (refundedAmount > 0) {
    lineItems.push({
      amount: -refundedAmount,
      label: 'Refunded Amount',
    });
  }

  const voidedAmount = payments
    .filter(p => p.status === 'VOIDED')
    .reduce(
      (acc, payment) => acc + getPaymentAmountWithoutServiceFee(payment),
      0,
    );
  if (voidedAmount) {
    lineItems.push({
      amount: -voidedAmount,
      label: 'Voided Amount',
    });
  }

  if (!lineItems.length) {
    lineItems.push({
      amount: 0,
      label: appointment ? 'Appointment Price' : 'Total Price',
    });
  }

  const totalPaid = payments
    .filter(p => p.status === 'SUCCESS')
    .reduce(
      (acc, payment) => acc + getPaymentAmountWithoutServiceFee(payment),
      0,
    );
  lineItems.push({
    amount: totalPaid,
    label: 'Total Paid',
    isTotal: true,
  });

  return lineItems;
}

export function getSplitPaymentsAmountAlreadyAdded(
  paymentMethods: Pick<SplitPaymentMethod, 'amount'>[],
  currentIndex: number,
) {
  const otherPaymentMethods = paymentMethods.slice(0, currentIndex);
  return otherPaymentMethods.reduce((acc, curr) => acc + curr.amount, 0);
}

export function getSplitPaymentsForm(
  totalPriceCents: number,
  paymentMethods: Pick<SplitPaymentMethod, 'amount'>[],
  currentIndex: number,
): SplitPaymentForm {
  const amountAlreadyAdded = getSplitPaymentsAmountAlreadyAdded(
    paymentMethods,
    currentIndex,
  );
  const remainingAmount =
    Math.round(totalPriceCents - amountAlreadyAdded * 100) / 100;

  return {
    amount:
      amountAlreadyAdded && remainingAmount
        ? formatDollarAmount(remainingAmount, true, true).replace(/,/g, '')
        : '',
    paymentMethod: '' as CheckoutPaymentMethod,
  };
}

export function getCheckoutErrorMessage(data: FailureData) {
  let failureCode: string | string[] = data.failureCode?.split('_') || [];
  failureCode = (failureCode as string[]).map((word, index) => {
    if (index === 0) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }
    return word.toLowerCase();
  });
  failureCode = failureCode.join(' ');
  const tempFailureMessage = `Error message: ${failureCode}. ${data.failureMessage}`;
  return tempFailureMessage;
}

export function isTipDisplayed({
  values,
  appointment,
  user,
}: {
  values: CheckoutFormValues;
  user: LoadedUser | null;
  appointment?: Appointment | null;
}) {
  const isCreditCardPayment = isCardPaymentMethod(values.paymentMethod);
  const disableCreditTipping =
    user?.business.disableCreditTipping && isCreditCardPayment;

  return (
    (!values.paymentMethod || isTipAllowed(values.paymentMethod)) &&
    (!!appointment || values.employeeId) &&
    !disableCreditTipping
  );
}

export function getPaymentMethodText(paymentMethod: PaymentMethod) {
  const displayPaymentMethod =
    paymentMethod === 'TERMINAL' ? 'CREDIT' : paymentMethod;
  return convertSnakeToTitleCase(displayPaymentMethod);
}

export function getPaymentMethodFee(
  paymentMethod: CheckoutPaymentMethod,
  business: LoadedUser['business'] | null | undefined,
) {
  const methodFeeRate =
    (paymentMethod === 'TERMINAL'
      ? business?.inStoreCustomerServiceFeeRate
      : paymentMethod === 'CREDIT' || paymentMethod === 'MANUAL'
      ? business?.onlineCustomerServiceFeeRate
      : paymentMethod === 'CASH' ||
        paymentMethod === 'GIFT_CARD' ||
        paymentMethod === 'FREE'
      ? 0
      : DEFAULT_CUSTOMER_SERVICE_FEE) ?? DEFAULT_CUSTOMER_SERVICE_FEE;

  return methodFeeRate;
}

export function calculateFeeAmount(totalAmount: number, feeRate: number) {
  const min = feeRate <= 0 ? 0 : MINIMUM_FEE_CENTS / 100;
  return Math.max(min, totalAmount * feeRate);
}
